import { FieldValueSource } from 'features/libra/ontology/ontologyFieldTypes'
import {
  assetModelProvenanceKey,
  getInputProvenanceSource,
} from 'features/libra/stepComponents/ffs/inputProvenance'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useCallback } from 'react'

/**
 * The asset-model JSON field keys whose provenance both the Asset Model step and
 * the Min Thickness step display + edit, as a set — used to gate an inline
 * per-field source accessory so only these intrinsic inputs get one. These keys
 * form the `assetModel:` overlay keys; the Min Thickness read side
 * (`FfsMinimumThicknessStep`) resolves citations from the same keys.
 */
export const FFS_PROVENANCE_JSONFIELDS: ReadonlySet<string> = new Set([
  'radius',
  'wall_thickness',
  'design_pressure',
  'yield_strength',
  'joint_efficiency',
  'pressure_coefficient',
  'tsl',
])

export interface UseFfsInputProvenanceResult {
  /** True when the ticket is read-only (sources are display-only). */
  readOnly: boolean
  /** The persisted citation for an asset-model field, or null when uncited. */
  getFieldSource: (jsonField: string) => FieldValueSource | null
  /**
   * Persist a batch of `jsonField -> source` citations to the ticket overlay in
   * ONE write. Callers invoke this on an explicit save (NOT per keystroke) —
   * source edits are staged in the editor and flushed together when the user
   * saves, so the batch is all-or-nothing and awaitable.
   */
  saveFieldSources: (
    entries: Record<string, FieldValueSource | null>
  ) => Promise<void>
}

/**
 * Shared source-of-truth wiring for FFS asset-model input provenance. Scoped to
 * the ticket's `componentId`, it maps an asset-model `jsonField` to the ticket
 * overlay via `assetModelProvenanceKey`: `getFieldSource` reads the persisted
 * citation (for display in both the Asset Model and Min Thickness steps), and
 * `saveFieldSources` persists a batch on an explicit save (used by the Asset
 * Model editor's Save flush; the write self-guards on read-only).
 *
 * Must be called unconditionally (before any early return) in the consuming
 * step components — it wraps `useTicketAccess`.
 */
export function useFfsInputProvenance(
  componentId: string | undefined
): UseFfsInputProvenanceResult {
  const { inputProvenance, setInputSources, readOnly } = useTicketAccess()

  const getFieldSource = useCallback(
    (jsonField: string): FieldValueSource | null =>
      componentId
        ? getInputProvenanceSource(
            inputProvenance,
            assetModelProvenanceKey(componentId, jsonField)
          )
        : null,
    [componentId, inputProvenance]
  )

  const saveFieldSources = useCallback(
    (entries: Record<string, FieldValueSource | null>): Promise<void> => {
      if (!componentId) return Promise.resolve()
      const keyed: Record<string, FieldValueSource> = {}
      for (const [jsonField, source] of Object.entries(entries)) {
        keyed[assetModelProvenanceKey(componentId, jsonField)] = source
      }
      return setInputSources(keyed)
    },
    [componentId, setInputSources]
  )

  return { readOnly, getFieldSource, saveFieldSources }
}
